import React from 'react';
import './MessagesPage.css';
import { useApp } from '../../context/AppContext';
import RealtimeChatPanel from '../../components/RealtimeChatPanel/RealtimeChatPanel';

/**
 * Fan-side direct messages with the creator.
 *
 * Layout contract (matches the global admin workspace contract — see
 * AdminContentDashboard.css "GLOBAL ADMIN WORKSPACE LAYOUT CONTRACT"):
 *   .messages-page = full available viewport (no max-width centering)
 *   header row     = creator card with avatar + online state
 *   chat workspace = grows to fill remaining height, scrolls internally
 */
const MessagesPage: React.FC = () => {
  const { adminUser, currentUser, setActiveTab, setIsAdminView, isAdminView } = useApp();

  // Use isAdminView (the UI toggle), not currentUser.isAdmin (the role).
  // That way "View as Subscriber" mode shows the fan's actual chat UI,
  // not the dead-end Hub redirect — admins testing the subscriber side
  // can see what fans see. The redirect still fires when the admin
  // genuinely sits in admin mode and accidentally navigates here.
  const partnerId = isAdminView ? '' : currentUser.id;

  if (!partnerId) {
    // Admin landed on the fan-side messages route (likely from preview
    // mode). Give them a one-tap path back to the Creator Hub inbox
    // instead of a copy-only dead end.
    return (
      <div className="messages-page">
        <div className="messages-empty-shell">
          <div className="messages-empty-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
            </svg>
          </div>
          <h3>Creator inbox lives in the Hub</h3>
          <p>Subscriber conversations and tools are in the Creator Hub. Jump back to manage replies.</p>
          <button
            className="messages-cta-primary"
            onClick={() => {
              setIsAdminView?.(true);
              setActiveTab('home');
            }}
          >
            Open Creator Hub →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="messages-page">
      <header className="messages-creator-bar">
        {adminUser.coverImage && (
          <img src={adminUser.coverImage} alt="" className="messages-cover" />
        )}
        <div className="messages-cover-overlay" />

        {/* Back arrow — always visible on mobile, navigates to Home tab.
            Without this the subscriber gets stuck inside the chat surface
            and has nothing but the bottom nav to escape. */}
        <button
          className="messages-back-btn"
          onClick={() => setActiveTab('home')}
          aria-label="Back to home"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
            <path d="M15 18l-6-6 6-6"/>
          </svg>
        </button>

        <div className="messages-creator-inner">
          <div className="messages-avatar-wrap">
            <img
              src={adminUser.avatar}
              alt={adminUser.displayName}
              className="messages-avatar"
              onError={e => { (e.target as HTMLImageElement).style.opacity = '0.4'; }}
            />
            {adminUser.online && <span className="messages-online-dot" title="Online" />}
          </div>
          <div className="messages-creator-meta">
            <h2>
              {adminUser.displayName}
              <svg viewBox="0 0 24 24" fill="currentColor" className="messages-verified" aria-hidden="true">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
            </h2>
            <p>
              {adminUser.online ? (
                <><span className="status-dot online" /> Online · usually replies fast</>
              ) : (
                <><span className="status-dot offline" /> Offline · will reply soon</>
              )}
            </p>
          </div>
        </div>
      </header>

      <div className="messages-workspace">
        <RealtimeChatPanel
          partnerId={partnerId}
          partnerName={adminUser.displayName}
          partnerAvatar={adminUser.avatar}
          currentUserId={currentUser.id}
          isAdmin={isAdminView}
        />
      </div>
    </div>
  );
};

export default MessagesPage;
